import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { getCookie } from "../../utils/CookieHelper";
import { COOKIE_ENUM, OrderTypesEnum, URL_PARAMS } from "../../utils/enums";
import { FranchiseDeliveryConfig } from "../../utils/types";
import { getFranchiseIsOpenData } from "../redux/http/getFranchiseConfig";
import {
  checkFranchiseCartRedux,
  resetSelectedScheduleOptions,
} from "../redux/slices/cartAndLoyaltySlice";
import { resetCartAction } from "../redux/slices/cartLoyaltyV2Slice";
import {
  clearCartDueToRestaurantSwitchFlag,
  setRestaurantSwitchInProgress,
  setSelectedFranchise,
} from "../redux/slices/configSlice";
import { setCurrentDeliveryConfig } from "../redux/slices/deliverySlice";
import { setBottomSheetState } from "../redux/slices/globalComponentsToggleSlice";
import { useAppDispatch, useAppSelector } from "./useReduxHooks";
import { findFranchiseBySlug } from "../../utils/help/franchise/findFranchiseBySlug";
import { selectCurrentDeliveryConfig } from "../redux/selectors/deliverySelectors";

const useFranchiseData = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { franchise, order_type } = router.query;

  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  const mainFranchise = useAppSelector(
    (state) => state.config.restaurantInfo?.mainFranchise
  );
  const hasWelcomeDrawerV3 = useAppSelector(
    (state) => state.config.restaurantConfig?.hasWelcomeDrawerV3
  );
  const activeFranchises = useAppSelector(
    (state) => state.config.activeFranchises
  );

  const currentDeliveryConfig = useAppSelector(selectCurrentDeliveryConfig);
  const token = useAppSelector((state) => state.userInfo.token);

  const orderTypeParamState = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );

  const cartAndLoyalty = useAppSelector((state) => state.cartAndLoyaltyReducer);

  const selfService = useAppSelector(
    (state) => state.franchiseStatus.selfService
  );

  const shouldClearCartDueToRestaurantSwitch = useAppSelector(
    (state) => state.config.shouldClearCartDueToRestaurantSwitch
  );

  /**
   * Read straight off the URL: the open below runs on mount, long before the
   * branch reaches redux, so `useDineInMenuView` has nothing to go on yet.
   *
   * The `franchise` param only - not getFranchiseSlug, which falls back to
   * window.location and would throw while pre-rendering. A restaurant that
   * carries its branch in the subdomain instead simply misses this shortcut and
   * falls back to the guard on the menu screen.
   */
  const urlIsDineInMenuView =
    order_type === OrderTypesEnum.DINE_IN &&
    typeof franchise === "string" &&
    Boolean(
      findFranchiseBySlug(
        activeFranchises?.filter((f) => f.live),
        franchise
      )?.franchiseConfig?.dineInMenuView
    );

  useEffect(() => {
    if (!getCookie(COOKIE_ENUM.TOKEN)) {
      return;
    }
    if (selectedFranchise?.id) {
      dispatch(checkFranchiseCartRedux(selectedFranchise?.id));
      if (cartAndLoyalty.cart.franchiseId !== selectedFranchise.id) {
        dispatch(resetCartAction());
      }
      dispatch(
        getFranchiseIsOpenData({
          franchiseId: selectedFranchise?.id,
          orderType: orderTypeParamState,
        })
      );
    }
  }, [selectedFranchise?.id, mainFranchise?.id, token]);

  useEffect(() => {
    if (!selfService) {
      const lastSelectedFranchise = Cookies.get(
        COOKIE_ENUM.LAST_SELECTED_FRANCHISE
      );
      // Don't re-open the welcome drawer if the customer explicitly skipped it
      // this session — otherwise skipping on the location screen (which
      // navigates here) would immediately prompt again (prepits/project#3913).
      const welcomeDrawerSkipped = Cookies.get(
        COOKIE_ENUM.WELCOME_DRAWER_SKIPPED
      );
      // Static dine-in menu (prepits/project#4291): the QR link already names
      // the branch and the order type, so there is nothing to ask. It never
      // sets LAST_SELECTED_FRANCHISE, so without this the drawer opens on every
      // load. Answered here rather than closed again later, so the sheet cannot
      // vanish from under a guest mid-navigation.
      if (
        !lastSelectedFranchise &&
        !welcomeDrawerSkipped &&
        !urlIsDineInMenuView
      ) {
        dispatch(setBottomSheetState(true));
      }
    }
  }, []);

  useEffect(() => {
    // Skip if router is not ready to prevent race conditions
    if (!router.isReady) {
      return;
    }

    // Only resolve from the URL when a franchise slug is actually present.
    // Without this guard a missing `?franchise` (undefined) matches the
    // slug-less main franchise (undefined === undefined), which then reverted a
    // chosen branch back to the main one (review: franchise cleared when the
    // URL lacks ?franchise).
    // Collision-aware: partner restaurants can share a slug (Vasko "B02"), and
    // the branch the guest just committed must win over the first list match.
    const franchiseFromSlug = franchise
      ? findFranchiseBySlug(
          activeFranchises,
          franchise as string,
          selectedFranchise
        )
      : undefined;

    if (
      selectedFranchise?.id !== franchiseFromSlug?.id ||
      !selectedFranchise?.id
    ) {
      const currentPath = window.location.pathname;
      const regexPattern = new RegExp(`^(/.*)?/${URL_PARAMS.TABLE}$`);

      if (currentPath === "/" || regexPattern.test(currentPath)) {
        // dispatch(setSelectedFranchise(franchiseFromSlug
      } else {
        if (franchiseFromSlug && order_type && order_type !== "undefined") {
          dispatch(setSelectedFranchise(franchiseFromSlug));
        }
      }
    }
    // Trigger on the URL params this effect reads (`franchise`/`order_type`),
    // NOT on `selectedFranchise?.id`. This is a URL→redux sync: it must run when
    // the URL changes. Keying it on the redux franchise instead created a race —
    // e.g. the welcome drawer dispatches the newly picked branch and then does an
    // async shallow `router.push`; this effect fired on the redux change while
    // `franchise` still held the previous slug, resolved `franchiseFromSlug` to
    // the OLD branch, and reverted the selection back to it.
    //
    // pathname + activeFranchises stay load-bearing under React 18 batching:
    // the params can settle while still on "/" (where this effect deliberately
    // does not apply the URL franchise) — the param VALUE doesn't change across
    // the redirect to /home, so only pathname re-fires the sync there. And the
    // franchise list can arrive after the URL settled. pathname, not asPath:
    // firing on every query-string change would re-enter this effect
    // throughout the pay/tip flows.
  }, [
    franchise,
    order_type,
    mainFranchise?.id,
    activeFranchises.length,
    router.isReady,
    router.pathname,
  ]);

  useEffect(() => {
    if (selectedFranchise?.franchiseConfig.deliveryV2) {
      const newFranchiseDeliveryConfig =
        selectedFranchise.franchiseConfig.deliveryV2.find(
          (deliveryConfig: FranchiseDeliveryConfig) =>
            deliveryConfig.deliveryType.id ===
            currentDeliveryConfig?.deliveryType.id
        );
      // In hasWelcomeDrawerV3 every flow commits selectedFranchise together
      // with a delivery config valid for it, so this franchise-change remap
      // must never null the config: `currentDeliveryConfig` isn't a
      // dependency here, so a transient (new franchise + stale config) render
      // — e.g. switching Maadi/car-pickup → Smart/cabin — would otherwise clear
      // the just-committed config, and null is a stable fixpoint it can't
      // recover from. Only remap when an equivalent config exists; otherwise
      // leave the explicitly-set config untouched.
      if (hasWelcomeDrawerV3) {
        if (newFranchiseDeliveryConfig) {
          dispatch(setCurrentDeliveryConfig(newFranchiseDeliveryConfig));
        }
      } else {
        dispatch(setCurrentDeliveryConfig(newFranchiseDeliveryConfig || null));
      }
    }

    if (
      selectedFranchise &&
      !selectedFranchise?.franchiseConfig?.scheduledOrder
    ) {
      dispatch(resetSelectedScheduleOptions());
    }
  }, [selectedFranchise?.id]);

  // Handle cart clearing when switching between partner restaurants
  useEffect(() => {
    if (shouldClearCartDueToRestaurantSwitch) {
      console.log("Clearing cart due to partner restaurant switch");
      dispatch(resetCartAction());
      dispatch(clearCartDueToRestaurantSwitchFlag());

      // Clear the restaurant switch in progress flag after a short delay
      // to allow state updates to settle
      setTimeout(() => {
        dispatch(setRestaurantSwitchInProgress(false));
      }, 100);
    }
  }, [shouldClearCartDueToRestaurantSwitch]);
};

export default useFranchiseData;
